import { fetchAuthSession } from "aws-amplify/auth";
import {
  BillingContext,
  BillingCustomer,
  CustomerBillingItem,
  IntegratorBilling,
  MasterPlan,
  PlanConfiguration,
  RetailPlan,
  createMasterPlan,
  createRetailPlan,
  getBillingContext,
  getCustomerBillingView,
  listBillingCustomers,
  listIntegrators,
  listMasterPlans,
  listRetailPlans,
  saveAiMonitoringRetailPricing,
  saveIntegratorEntitlements,
  updateMasterPlan,
  updateRetailPlan,
} from "./billingService";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type HierarchyRetailPlan = RetailPlan & {
  masterPlanId?: string;
  wholesaleMonthlyPrice?: number;
};

export type HierarchyIntegratorBilling = IntegratorBilling & {
  retailPlans?: HierarchyRetailPlan[];
};

export type HierarchyBillingCustomer = BillingCustomer & {
  billingRetailPlanIds?: string[];
};

export {
  createMasterPlan,
  createRetailPlan,
  getBillingContext,
  getCustomerBillingView,
  listMasterPlans,
  listRetailPlans,
  saveAiMonitoringRetailPricing,
  saveIntegratorEntitlements,
  updateMasterPlan,
  updateRetailPlan,
};

export type {
  BillingContext,
  CustomerBillingItem,
  MasterPlan,
  PlanConfiguration,
  RetailPlan,
};

async function token(): Promise<string> {
  const session = await fetchAuthSession();
  const value = session.tokens?.idToken?.toString();
  if (!value) {
    throw new Error("Your session has expired. Sign out and sign in again.");
  }
  return value;
}

async function readResponse(response: Response) {
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        text ||
        `Billing request failed (${response.status})`,
    );
  }
  return payload;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const idToken = await token();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });
  return readResponse(response) as Promise<T>;
}

export async function listHierarchyIntegrators(): Promise<
  HierarchyIntegratorBilling[]
> {
  return (await listIntegrators()) as HierarchyIntegratorBilling[];
}

export async function listHierarchyCustomers(): Promise<
  HierarchyBillingCustomer[]
> {
  return (await listBillingCustomers()) as HierarchyBillingCustomer[];
}

export async function setAdminBillingPlans(
  adminId: string,
  retailPlanIds: string[],
) {
  const payload = await api<{
    admin: { customerId: string; billingRetailPlanIds: string[] };
  }>(`/billing/admins/${encodeURIComponent(adminId)}/plans`, {
    method: "PUT",
    body: JSON.stringify({ retailPlanIds }),
  });
  return payload.admin;
}

function chooseMainH264Profile(profiles: any[]) {
  const candidates = (Array.isArray(profiles) ? profiles : []).filter(
    (profile) =>
      String(profile?.encoding || "").trim().toUpperCase() === "H264" &&
      profile?.profileSelectable !== false,
  );

  const score = (profile: any) => {
    const label = `${profile?.profileName || ""} ${profile?.profileToken || ""}`
      .toLowerCase()
      .replace(/[_-]+/g, " ");
    const named = /\bmain\s*stream\b/.test(label)
      ? 3
      : /\bmain\b/.test(label)
        ? 2
        : /\bprofile\s*1\b/.test(label)
          ? 1
          : 0;
    const pixels =
      Number(profile?.current?.resolution?.width || 0) *
      Number(profile?.current?.resolution?.height || 0);
    return { named, pixels };
  };

  candidates.sort(
    (left, right) =>
      score(right).named - score(left).named ||
      score(right).pixels - score(left).pixels,
  );

  if (!candidates.length) {
    throw new Error(
      "This camera does not expose a selectable H.264 main stream required by the selected plan.",
    );
  }
  return candidates[0];
}

async function applyCameraProfile(
  cameraId: string,
  configuration: PlanConfiguration,
) {
  const idToken = await token();
  const url = `${API_BASE}/cameras/${encodeURIComponent(cameraId)}/video-settings`;
  const discoveryResponse = await fetch(url, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });
  const discovery = await readResponse(discoveryResponse);
  const profile = chooseMainH264Profile(discovery?.profiles);
  if (!profile?.profileToken) {
    throw new Error("The selected camera main stream has no ONVIF profile token.");
  }

  const applyResponse = await fetch(url, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      profileToken: profile.profileToken,
      settings: {
        resolution: configuration.resolution,
        fps: configuration.fps,
        bitrateKbps: configuration.bitrateKbps,
      },
    }),
  });
  await readResponse(applyResponse);
}

export async function assignHierarchyCameraPlan(input: {
  cameraId: string;
  configuration: PlanConfiguration;
  masterPlanId?: string;
  retailPlanId?: string;
  customerId?: string;
}) {
  if (input.configuration.billingModel !== "PAYG") {
    await applyCameraProfile(input.cameraId, input.configuration);
  }
  return api<{
    camera: any;
    plan: MasterPlan | RetailPlan;
    retentionSync: any;
  }>("/billing/camera-plan", {
    method: "POST",
    body: JSON.stringify({
      cameraId: input.cameraId,
      ...(input.masterPlanId ? { masterPlanId: input.masterPlanId } : {}),
      ...(input.retailPlanId ? { retailPlanId: input.retailPlanId } : {}),
      ...(input.customerId ? { customerId: input.customerId } : {}),
    }),
  });
}
